/**
 * Générateur de prompt système pour l'API Claude
 * Extrait les informations du JSON de flow pour créer un prompt optimisé
 */

import { consoleError } from '@/utils/logger';

export class ClaudePromptGenerator {
  
  /**
   * Génère un système prompt optimisé basé sur le JSON de flow
   * @param {Object} flowData - Le JSON contenant les données du flow
   * @returns {String} Le système prompt formaté pour Claude
   */
  static generateOptimizedSystemPrompt(flowData) {
    if (!flowData?.flow) {
      throw new Error('Format de données invalides : flow manquant');
    }

    const chatbotInfo = flowData.chatbotInfo || {};
    const blocks = flowData.flow.blocks || [];
    const mainBlocks = flowData.flow.mainblocks || {};
    
    // Extraire les informations de chaque bloc
    const blockInfos = this.extractDetailedBlockInformations(blocks, mainBlocks);
    
    // Générer le prompt système optimisé avec les données de flow complètes (incluant les produits)
    const systemPrompt = this.buildOptimizedSystemPrompt(blockInfos, chatbotInfo, flowData);
    
    return systemPrompt;
  }

  /**
   * Calcule le nombre approximatif de tokens dans un texte
   * Basé sur l'encodage BPE utilisé par Claude/OpenAI
   * @param {String} text - Le texte à analyser
   * @returns {Number} Nombre approximatif de tokens
   */
  static estimateTokenCount(text) {
    if (!text || typeof text !== 'string') {
      return 0;
    }

    // Normaliser le texte
    const normalizedText = text.trim();
    
    if (normalizedText.length === 0) {
      return 0;
    }

    // Estimation basée sur plusieurs heuristiques
    
    // 1. Estimation de base : ~4 caractères par token pour l'anglais, ~3 pour le français
    const basicEstimate = normalizedText.length / 3.5;
    
    // 2. Compter les mots (tokens souvent alignés sur les mots)
    const words = normalizedText.split(/\s+/).filter(word => word.length > 0);
    const wordBasedEstimate = words.length * 1.3; // Facteur pour les sous-mots
    
    // 3. Compter les caractères spéciaux et la ponctuation (souvent des tokens séparés)
    const specialChars = (normalizedText.match(/[.,!?;:()\[\]{}"'`~@#$%^&*+=<>\/\\|_-]/g) || []).length;
    const specialCharTokens = specialChars * 0.8;
    
    // 4. Compter les espaces et nouvelles lignes
    const whitespaceTokens = (normalizedText.match(/\s/g) || []).length * 0.1;
    
    // 5. Ajustement pour le JSON et code (plus de tokens)
    let jsonAdjustment = 0;
    if (normalizedText.includes('{') || normalizedText.includes('[') || normalizedText.includes('"type"')) {
      jsonAdjustment = normalizedText.length * 0.1; // 10% de tokens supplémentaires pour JSON
    }
    
    // 6. Ajustement pour les variables et formatage spécial
    const variableMatches = (normalizedText.match(/\{\{[^}]+\}\}/g) || []).length;
    const variableTokens = variableMatches * 3; // Variables comptent comme ~3 tokens
    
    // Combiner les estimations avec des poids
    const weightedEstimate = (
      basicEstimate * 0.4 +
      wordBasedEstimate * 0.3 +
      specialCharTokens * 0.1 +
      whitespaceTokens * 0.05 +
      jsonAdjustment * 0.1 +
      variableTokens * 0.05
    );
    
    // Arrondir et s'assurer d'un minimum de 1 token pour un texte non-vide
    return Math.max(1, Math.round(weightedEstimate));
  }

  /**
   * Analyse les coûts du system prompt avec différents modèles Claude
   * @param {String} systemPrompt - Le prompt système à analyser
   * @returns {Object} Analyse des coûts pour différents modèles
   */
  static analyzePromptCosts(systemPrompt) {
    const tokenCount = this.estimateTokenCount(systemPrompt);
    
    // Tarifs par 1K tokens (approximatifs, à vérifier selon la documentation officielle)
    const models = {
      'claude-3-opus-20240229': {
        name: 'Claude 3 Opus',
        input: 0.015,   // $0.015 per 1K input tokens
        output: 0.075,  // $0.075 per 1K output tokens
        maxContext: 200000
      },
      'claude-3-sonnet-20240229': {
        name: 'Claude 3 Sonnet',
        input: 0.003,   // $0.003 per 1K input tokens
        output: 0.015,  // $0.015 per 1K output tokens
        maxContext: 200000
      },
      'claude-3-haiku-20240307': {
        name: 'Claude 3 Haiku',
        input: 0.00025, // $0.00025 per 1K input tokens
        output: 0.00125, // $0.00125 per 1K output tokens
        maxContext: 200000
      }
    };

    const costAnalysis = {
      systemPrompt: {
        length: systemPrompt.length,
        estimatedTokens: tokenCount,
        percentOfContext: {}
      },
      models: {},
      recommendations: []
    };

    // Calculer les coûts pour chaque modèle
    Object.entries(models).forEach(([modelId, modelInfo]) => {
      const inputCostPer1K = modelInfo.input;
      const outputCostPer1K = modelInfo.output;
      
      // Coût du system prompt (input)
      const systemPromptCost = (tokenCount / 1000) * inputCostPer1K;
      
      // Estimation coûts pour différents volumes d'usage
      const usageScenarios = {
        light: { calls: 100, avgOutputTokens: 150 },    // 100 appels, 150 tokens de réponse
        medium: { calls: 1000, avgOutputTokens: 200 },  // 1000 appels, 200 tokens de réponse
        heavy: { calls: 10000, avgOutputTokens: 250 }   // 10k appels, 250 tokens de réponse
      };

      const scenarios = {};
      Object.entries(usageScenarios).forEach(([scenario, usage]) => {
        const inputCost = systemPromptCost * usage.calls;
        const outputCost = (usage.avgOutputTokens / 1000) * outputCostPer1K * usage.calls;
        scenarios[scenario] = {
          calls: usage.calls,
          inputCost: inputCost,
          outputCost: outputCost,
          totalCost: inputCost + outputCost,
          costPerCall: (inputCost + outputCost) / usage.calls
        };
      });

      costAnalysis.models[modelId] = {
        name: modelInfo.name,
        systemPromptCost: systemPromptCost,
        scenarios: scenarios
      };

      // Calculer le pourcentage du contexte utilisé
      costAnalysis.systemPrompt.percentOfContext[modelId] = 
        ((tokenCount / modelInfo.maxContext) * 100).toFixed(2);
    });

    // Générer des recommandations
    if (tokenCount > 10000) {
      costAnalysis.recommendations.push({
        type: 'warning',
        message: `Prompt très long (${tokenCount} tokens). Considérez l'optimisation pour réduire les coûts.`
      });
    }

    if (tokenCount > 50000) {
      costAnalysis.recommendations.push({
        type: 'error',
        message: `Prompt extrêmement long (${tokenCount} tokens). Risque de dépassement de contexte.`
      });
    }

    // Recommandation de modèle selon l'usage
    const lightUsageCostOpus = costAnalysis.models['claude-3-opus-20240229'].scenarios.light.totalCost;
    const lightUsageCostSonnet = costAnalysis.models['claude-3-sonnet-20240229'].scenarios.light.totalCost;
    const lightUsageCostHaiku = costAnalysis.models['claude-3-haiku-20240307'].scenarios.light.totalCost;

    if (lightUsageCostHaiku < 1) {
      costAnalysis.recommendations.push({
        type: 'info',
        message: 'Pour un usage léger, Claude 3 Haiku offre le meilleur rapport qualité/prix.'
      });
    } else if (lightUsageCostSonnet < 10) {
      costAnalysis.recommendations.push({
        type: 'info',
        message: 'Pour un usage moyen, Claude 3 Sonnet offre un bon équilibre performance/coût.'
      });
    }

    return costAnalysis;
  }

  /**
   * Génère un rapport détaillé des coûts avec formatage pour l'affichage
   * @param {String} systemPrompt - Le prompt système
   * @returns {Object} Rapport formaté pour l'interface utilisateur
   */
  static generateCostReport(systemPrompt) {
    const analysis = this.analyzePromptCosts(systemPrompt);
    
    const report = {
      summary: {
        promptLength: analysis.systemPrompt.length,
        estimatedTokens: analysis.systemPrompt.estimatedTokens,
        avgTokensPerChar: (analysis.systemPrompt.estimatedTokens / analysis.systemPrompt.length).toFixed(3)
      },
      models: [],
      recommendations: analysis.recommendations,
      contextUsage: analysis.systemPrompt.percentOfContext
    };

    // Formater les informations des modèles
    Object.entries(analysis.models).forEach(([modelId, modelData]) => {
      report.models.push({
        id: modelId,
        name: modelData.name,
        systemPromptCost: `$${modelData.systemPromptCost.toFixed(6)}`,
        scenarios: {
          light: {
            description: '100 appels/mois',
            totalCost: `$${modelData.scenarios.light.totalCost.toFixed(4)}`,
            costPerCall: `$${modelData.scenarios.light.costPerCall.toFixed(6)}`
          },
          medium: {
            description: '1,000 appels/mois', 
            totalCost: `$${modelData.scenarios.medium.totalCost.toFixed(3)}`,
            costPerCall: `$${modelData.scenarios.medium.costPerCall.toFixed(5)}`
          },
          heavy: {
            description: '10,000 appels/mois',
            totalCost: `$${modelData.scenarios.heavy.totalCost.toFixed(2)}`,
            costPerCall: `$${modelData.scenarios.heavy.costPerCall.toFixed(5)}`
          }
        }
      });
    });

    return report;
  }

  /**
   * Formate un bloc individuel pour l'entraînement/ajout au prompt
   * @param {Object} block - Le bloc à formater
   * @returns {String} Le contenu textuel du bloc formaté pour le prompt
   */
  static formatBlockForPrompt(block) {
    // Utiliser processBlock pour extraire les infos
    // Si block.source n'est pas défini, on assume 'standard' par défaut
    const info = this.processBlock(block, block.source || 'standard');
    
    // Construire le string formaté comme dans buildOptimizedSystemPrompt
    let promptChunk = `BLOC "${info.idblock}"
   Nom: ${info.blockname}
   ${info.description ? `Commentaire: ${info.description}\n   ` : ''}
   Type: ${info.source} 
   Contenu: ${info.contextualContent}`;
      
    if (info.buttons.length > 0) {
      promptChunk += `\n   Boutons disponibles: ${info.buttons.map(btn => `"${btn.title}" (ID: ${btn.id})`).join(', ')}`;
    }
    
    promptChunk += '\n';
    
    return promptChunk;
  }



  /**
   * Extrait les informations détaillées de chaque bloc
   * @param {Array} blocks - Liste des blocs
   * @param {Object} mainBlocks - Blocs principaux
   * @returns {Array} Informations structurées des blocs
   */
  static extractDetailedBlockInformations(blocks, mainBlocks) {

    // Debug logs supprimés pour éviter les surcharges
// console.log('extractDetailedBlockInformations ===>')
// console.log('TAILLE blocks ===>',blocks.length)

    const allBlocks = [];
    
    // Traiter les blocs principaux (welcome, default, etc.)
    Object.entries(mainBlocks).forEach(([type, blockArray]) => {
      if (Array.isArray(blockArray)) {
        blockArray.forEach(block => {
          allBlocks.push(this.processBlock(block, `mainblock_${type}`));
        });
      }
    });
    
    // Traiter les blocs standards
    blocks.forEach(block => {
      allBlocks.push(this.processBlock(block, 'standard'));
    });
    
    return allBlocks.filter(block => block.hasRelevantContent);
  }

  /**
   * Traite un bloc individuel pour extraire ses informations
   * @param {Object} block - Le bloc à traiter
   * @param {String} source - Source du bloc
   * @returns {Object} Informations structurées du bloc
   */
  static processBlock(block, source) {
    const info = {
      idblock: block.idblock,
      blockname: block.blockname || 'Sans nom',
      type: block.type || 'standard',
      description: block.description , 
      source: source, 
      textMessages: [],
      buttons: [],
      hasAnswerElement: false,
      hasRedirection: false,
      redirectionTarget: null,
      hasRelevantContent: false,
      elementTypes: [],
      contextualContent: ''
    };

    // Analyser le flow du bloc
    if (block.flow && Array.isArray(block.flow)) {
      block.flow.forEach(element => {
        info.elementTypes.push(element.type);
        
        switch (element.type) {
          case 'text':
            if (element.message && element.message.trim()) {
              info.textMessages.push(element.message.trim());
              info.hasRelevantContent = true;
            }
            break;
            
          case 'text_button':
            if (element.message && element.message.trim()) {
              info.textMessages.push(element.message.trim());
              info.hasRelevantContent = true;
            }
            if (element.buttons && Array.isArray(element.buttons)) {
              element.buttons.forEach(btn => {
                info.buttons.push({
                  id: btn.id,
                  title: btn.title,
                  payload: btn.payload || null
                });
              });
              info.hasRelevantContent = true;
            }
            break;
            
          case 'answer':
            info.hasAnswerElement = true;
            info.hasRelevantContent = true;
            break;
            
          case 'redirection':
            info.hasRedirection = true;
            info.redirectionTarget = element.redirection_block;
            break;
            
          case 'list':
            info.hasRelevantContent = true;
            if (element.interactive) {
                const i = element.interactive;
                let listDesc = "Liste Interactive:";
                if (i.header?.text) listDesc += ` [Titre] ${i.header.text}`;
                if (i.body?.text) listDesc += ` [Message] ${i.body.text}`;
                if (i.action?.button) listDesc += ` [Bouton] ${i.action.button}`;
                
                if (i.action?.sections && Array.isArray(i.action.sections)) {
                    const options = [];
                    i.action.sections.forEach(sec => {
                        if (sec.rows) {
                            sec.rows.forEach(row => {
                                options.push(`"${row.title}" (ID: ${row.id})`);
                            });
                        }
                    });
                    if (options.length > 0) {
                        listDesc += ` | Options: ${options.join(', ')}`;
                    }
                }
                // Store specifically as a special content type to avoid truncation if needed, 
                // but simpler to push to textMessages if we relax truncation logic or separate it.
                // Let's attach it to buttons-like logic or separate field.
                info.listOptions = listDesc;
            }
            break;

          case 'condition':
            info.hasRelevantContent = true;
            // Format condition for AI context
            let conditionText = "Logique Conditionnelle: ";
            element.conditions.forEach((cond, idx) => {
               conditionText += `${idx > 0 ? ' SINON ' : ''}SI [${cond.variable}] ${cond.operator} ${cond.value} ALORS aller vers "${cond.next_block}"`;
            });
            if (element.default_block) {
                conditionText += ` SINON aller vers "${element.default_block}"`;
            }
            info.textMessages.push(conditionText);
            break;

          case 'variable_insert':
             info.hasRelevantContent = true;
             info.textMessages.push(`Action System: Définir variable [${element.variable_name}] = "${element.value}"`);
             break;

          case 'location_request_message':
             info.hasRelevantContent = true;
             info.textMessages.push(`Demande de localisation: ${element.message || 'Envoyer votre position'}`);
             break;
        }
      });
    }
    
    // Générer le contenu contextuel
    info.contextualContent = this.generateContextualContent(info);
    
    return info;
  }

  /**
   * Génère un contenu contextuel pour un bloc
   * @param {Object} blockInfo - Informations du bloc
   * @returns {String} Contenu contextuel
   */
  static generateContextualContent(blockInfo) {
    const contexts = [];
    
    // Ajouter le nom du bloc
    if (blockInfo.blockname && blockInfo.blockname !== 'Sans nom') {
      contexts.push(`Bloc: ${blockInfo.blockname}`);
    }
    
    // Ajouter les messages texte
    if (blockInfo.textMessages.length > 0) {
      const firstMessage = blockInfo.textMessages[0];
      // Nettoyer le message des variables et formatage
      const cleanMessage = firstMessage
        .replace(/\{\{[^}]+\}\}/g, '[variable]')
        .replace(/\*/g, '')
        .replace(/\n+/g, ' ')
        .substring(0, 200);
      contexts.push(`Contenu: ${cleanMessage}`);
    }
    
    // Ajouter les boutons disponibles
    if (blockInfo.buttons.length > 0) {
      const buttonTitles = blockInfo.buttons.map(btn => btn.title).join(', ');
      contexts.push(`Options: ${buttonTitles}`);
    }

    // Ajouter les détails de la liste interactive
    if (blockInfo.listOptions) {
        contexts.push(blockInfo.listOptions);
    }
    
    // Ajouter des informations sur les éléments spéciaux
    if (blockInfo.hasAnswerElement) {
      contexts.push('Collecte de réponse utilisateur');
    }
    
    if (blockInfo.hasRedirection && blockInfo.redirectionTarget) {
      contexts.push(`Redirige vers: ${blockInfo.redirectionTarget}`);
    }
    
    return contexts.join(' | ');
  }

  /**
   * Construit le prompt système optimisé
   * @param {Array} blockInfos - Informations des blocs
   * @param {Object} chatbotInfo - Informations du chatbot
   * @param {Object} flowData - Données complètes du flow (pour accéder aux produits)
   * @returns {String} Prompt système formaté
   */
  static buildOptimizedSystemPrompt(blockInfos, chatbotInfo, flowData = null) {
    const botName = chatbotInfo.bot_name || 'Assistant';
    const botPhone = chatbotInfo.telBot || '';
    const catalogLink = chatbotInfo.catalogLink || '';
    
    let prompt = `Tu es ${botName}, un assistant IA intelligent spécialisé dans la gestion des conversations et la redirection vers les bons contenus.

=== INFORMATIONS DU CHATBOT ===
Nom: ${botName}
${botPhone ? `Téléphone: ${botPhone}` : ''}
${catalogLink ? `Catalogue: ${catalogLink}` : ''}

{{INFOS_VARIABLES}}

=== BLOCS DISPONIBLES ===
Voici tous les blocs de conversation disponibles avec leurs identifiants et contenus :

`;

    // Ajouter chaque bloc avec ses détails
    blockInfos.forEach((block, index) => {
      prompt += `${index + 1}. BLOC "${block.idblock}"
   Nom: ${block.blockname}
   ${block.description ? `Commentaire: ${block.description}\n   ` : ''}
   Type: ${block.source} 
   Contenu: ${block.contextualContent}  
`;
      
      // Ajouter les boutons s'il y en a  
      if (block.buttons.length > 0) {
        prompt += `   Boutons disponibles: ${block.buttons.map(btn => `"${btn.title}" (ID: ${btn.id})`).join(', ')}\n`;
      }
      
      prompt += '\n';
    });

    // Ajouter les informations sur les produits si disponibles
    if (flowData && flowData.products && flowData.products.catalog && flowData.products.catalog.length > 0) {
      prompt += `

=== CATALOGUE PRODUITS ===
Tu as accès à un catalogue de ${flowData.products.totalProducts} produit(s) :

`;
      
      flowData.products.catalog.forEach((product, index) => {
        prompt += `${index + 1}. "${product.title}" (ID: ${product.product_retailer_id})
   - Prix: ${new Intl.NumberFormat('fr-FR').format(product.price)} ${product.currency}
   - Catégorie: ${product.category}
   - Stock: ${product.stock} unité(s)`;
        
        // Ajouter l'image de manière proéminente
        if (product.images && product.images[0]) {
          prompt += `
   - Image: ${product.images[0]}`;
        }
        
        prompt += `
   - Description: ${product.description}

`;
      });

      // Ajouter des statistiques du catalogue
      const categories = [...new Set(flowData.products.catalog.map(p => p.category))];
      const totalValue = flowData.products.catalog.reduce((sum, p) => sum + (p.price || 0), 0);
      
      prompt += ` 
💡 UTILISATION DES PRODUITS:
- Utilise ces informations pour répondre aux questions sur les produits
- Recommande des produits pertinents selon les demandes
- Fais référence aux catégories, prix et disponibilités
- Utilise les liens d'images pour enrichir tes réponses visuelles
- Quand tu mentionnes un produit, tu peux inclure son lien d'image dans ta réponse
- Pour plus de détails sur un produit, redirige vers un bloc de catalogue approprié

`;
    }

prompt += `=== RÈGLE SPÉCIALE POUR LES BLOCS WELCOME ===

🚨 IMPORTANT : Les blocs de type "welcome" ou "mainblock_welcome" ne doivent JAMAIS être utilisés en redirection.
✅ CORRECT : Utilise leur contenu pour inspirer tes réponses "text" ou "text_button"
❌ INCORRECT : {"type":"redirection","redirection_block":"bloc_welcome_id"}
❌ INCORRECT : Bouton avec ID pointant vers un bloc welcome

Quand l'utilisateur salue, utilise le CONTENU du bloc welcome comme inspiration pour créer une réponse "text" personnalisée, mais ne redirige JAMAIS vers ce bloc.

=== TYPES DE RÉPONSES AUTORISÉES ===

Tu DOIS répondre UNIQUEMENT avec l'un de ces 4 formats JSON (sans texte supplémentaire) :

1. TEXTE SIMPLE - Pour réponses directes :
{"type":"text","id_element":null,"id_previous":null,"message":"Votre réponse ici","preview_url":true}

2. TEXTE AVEC BOUTONS - Pour orienter vers des blocs existants :
{"type":"text_button","id_element":null,"id_previous":null,"message":"Votre message ici","buttons":[{"id":"idblock_existant","title":"Texte ≤20 chars"}]}

3. REDIRECTION DIRECTE - Pour rediriger immédiatement vers un bloc :
{"type":"redirection","id_element":null,"id_previous":null,"redirection_block":"idblock_existant"}

4. IMAGES - Pour l'affichage d'images (TOUJOURS en array avec delay et text/text_button) :
[
  {
    "type": "image",
    "id_element": "image_id_unique",
    "id_previous": null,
    "link": "https://lien-vers-votre-image.jpg"
  },
  {
    "type": "delay",
    "id_element": "delay_id_unique",
    "id_previous": "image_id_unique",
    "timmer": "1.5"
  },
  {
    "type": "text",
    "id_element": "text_id_unique",
    "id_previous": "delay_id_unique",
    "message": "Votre message d'accompagnement",
    "preview_url": true
  }
]


=== RÈGLES CRITIQUES ===

OBLIGATOIRE :
✅ Réponds aux salutations de manière chaleureuse
✅ Utilise uniquement les idblock qui existent dans la liste ci-dessus
✅ Les titres des boutons doivent faire maximum 20 caractères
✅ Maximum 3 boutons par réponse text_button
✅ Vérifie que le bloc de redirection correspond au contexte de la demande
✅ Pour les images : utilise les liens d'images des produits du catalogue quand approprié
✅ Images TOUJOURS en format array avec delay (1.5s) et message d'accompagnement
✅ Génère des id_element uniques pour chaque élément de l'array image

INTERDIT :
❌ Ne réponds PAS aux insultes (retourne une redirection vers l'agent)
❌ N'invente JAMAIS d'idblock qui n'existe pas
❌ Ne redirige pas vers un bloc dont le contenu ne correspond pas à la demande
❌ N'ajoute aucun texte en dehors du JSON
❌ JAMAIS de redirection vers un bloc de type "welcome" ou "mainblock_welcome"
❌ JAMAIS de bouton avec un ID pointant vers un bloc de type "welcome"

=== GESTION SPÉCIALE === 

- Si l'utilisateur demande à parler à un agent : cherche un bloc contenant "agent" dans son contenu
- Si aucun bloc ne correspond parfaitement : utilise le type "text" pour une réponse générale
- En cas d'incompréhension totale : propose de parler à un agent
- Pour les salutations : utilise le CONTENU du bloc welcome pour générer du "text" ou "text_button", JAMAIS de redirection
- Bloc de type "welcome" : Utilise son contenu comme base pour tes réponses de salutation mais ne redirige JAMAIS vers ce bloc

=== EXEMPLES DE BONNES RÉPONSES ===

Salutation utilisateur (utilise le contenu du bloc welcome mais en mode "text"):
{"type":"text","id_element":null,"id_previous":null,"message":"Bonjour ! Je suis ${botName}, comment puis-je vous aider aujourd'hui ?","preview_url":true}

Demande d'information avec bloc correspondant:
{"type":"redirection","id_element":null,"id_previous":null,"redirection_block":"catalogue-pro-pr1alj"}

Proposition d'options:
{"type":"text_button","id_element":null,"id_previous":null,"message":"Que souhaitez-vous faire ?","buttons":[{"id":"catalogue-pro-pr1alj","title":"Voir catalogue"},{"id":"point-de-coll-ed7kgb","title":"Points collecte"}]}

Affichage d'image produit:
[
  {
    "type": "image",
    "id_element": "img_prod_001",
    "id_previous": null,
    "link": "https://apicommerce.aipeelo.xyz/images/products/product_689220a9ac56ee97afa81097_1755378468106_c92669dbbbaf366d.jpeg"
  },
  {
    "type": "delay",
    "id_element": "delay_001",
    "id_previous": "img_prod_001",
    "timmer": "1.5"
  },
  {
    "type": "text",
    "id_element": "text_001",
    "id_previous": "delay_001",
    "message": "Voici le parfum Manguier Rose à 55 000 XOF",
    "preview_url": true
  }
]

Maintenant, analyse le message de l'utilisateur et réponds avec le JSON approprié.`;

    return prompt;
  }

  /**
   * Génère les blocs structurés (Array JSON)
   */
  static generateStructuredBlocks(blockInfos) {
      return blockInfos.map(block => {
          // Ensure we process the block to extract content
          const processed = this.processBlock(block, block.source || 'standard');
          
          return {
              idblock: processed.idblock,
              nom: processed.blockname,
              source: processed.source, 
              commentaire: processed.description || "",
              type: processed.type, 
              contenu: processed.contextualContent, // Now guaranteed to be populated
              buttons: processed.buttons 
          };
      });
  }

  /**
   * Génère les produits structurés (Array JSON)
   */
  static generateStructuredProducts(flowData) {
      if (!flowData?.products?.catalog) return [];
      
      return flowData.products.catalog.map(product => ({
          id: product.product_retailer_id,
          title: product.title,
          price: product.price,
          currency: product.currency,
          category: product.category,
          stock: product.stock,
          description: product.description,
          image: product.images?.[0] || ""
      }));
  }

  /**
   * Génère le template avec placeholders {{LIST_BLOCKS}} et {{LIST_PRODUCTS}}
   */
  static generateTemplate(chatbotInfo) {
    const botName = chatbotInfo.bot_name || 'Assistant';
    const botPhone = chatbotInfo.telBot || '';
    const catalogLink = chatbotInfo.catalogLink || '';

    // Base Template
    return `Tu es ${botName}, un assistant IA intelligent spécialisé dans la gestion des conversations et la redirection vers les bons contenus.

=== INFORMATIONS DU CHATBOT ===
Nom: ${botName}
${botPhone ? `Téléphone: ${botPhone}` : ''}
${catalogLink ? `Catalogue: ${catalogLink}` : ''}

{{INFOS_VARIABLES}}

=== BLOCS DISPONIBLES ===
Voici tous les blocs de conversation disponibles avec leurs identifiants et contenus :

{{LIST_BLOCKS}}

=== CATALOGUE PRODUITS ===
Tu as accès à un catalogue de produits :

{{LIST_PRODUCTS}}

=== TYPES DE RÉPONSES AUTORISÉES ===

Tu DOIS répondre UNIQUEMENT avec l'un de ces 4 formats JSON (sans texte supplémentaire) :

1. TEXTE SIMPLE - Pour réponses directes :
{"type":"text","id_element":null,"id_previous":null,"message":"Votre réponse ici","preview_url":true}

2. TEXTE AVEC BOUTONS - Pour orienter vers des blocs existants :
{"type":"text_button","id_element":null,"id_previous":null,"message":"Votre message ici","buttons":[{"id":"idblock_existant","title":"Texte ≤20 chars"}]}

3. REDIRECTION DIRECTE - Pour rediriger immédiatement vers un bloc :
{"type":"redirection","id_element":null,"id_previous":null,"redirection_block":"idblock_existant"}

4. IMAGES - Pour l'affichage d'images (TOUJOURS en array avec delay et text/text_button) :
[
  {
    "type": "image",
    "id_element": "image_id_unique",
    "id_previous": null,
    "link": "https://lien-vers-votre-image.jpg"
  },
  {
    "type": "delay",
    "id_element": "delay_id_unique",
    "id_previous": "image_id_unique",
    "timmer": "1.5"
  },
  {
    "type": "text",
    "id_element": "text_id_unique",
    "id_previous": "delay_id_unique",
    "message": "Votre message d'accompagnement",
    "preview_url": true
  }
]


=== RÈGLES CRITIQUES ===

OBLIGATOIRE :
✅ Réponds aux salutations de manière chaleureuse
✅ Utilise uniquement les idblock qui existent dans la liste ci-dessus
✅ Les titres des boutons doivent faire maximum 20 caractères
✅ Maximum 3 boutons par réponse text_button
✅ Vérifie que le bloc de redirection correspond au contexte de la demande
✅ Pour les images : utilise les liens d'images des produits du catalogue quand approprié
✅ Images TOUJOURS en format array avec delay (1.5s) et message d'accompagnement
✅ Génère des id_element uniques pour chaque élément de l'array image

INTERDIT :
❌ Ne réponds PAS aux insultes (retourne une redirection vers l'agent)
❌ N'invente JAMAIS d'idblock qui n'existe pas
❌ Ne redirige pas vers un bloc dont le contenu ne correspond pas à la demande
❌ N'ajoute aucun texte en dehors du JSON
❌ JAMAIS de redirection vers un bloc de type "welcome" ou "mainblock_welcome"
❌ JAMAIS de bouton avec un ID pointant vers un bloc de type "welcome"

=== GESTION SPÉCIALE === 

- Si l'utilisateur demande à parler à un agent : cherche un bloc contenant "agent" dans son contenu
- Si aucun bloc ne correspond parfaitement : utilise le type "text" pour une réponse générale
- En cas d'incompréhension totale : propose de parler à un agent
- Pour les salutations : utilise le CONTENU du bloc welcome pour générer du "text" ou "text_button", JAMAIS de redirection
- Bloc de type "welcome" : Utilise son contenu comme base pour tes réponses de salutation mais ne redirige JAMAIS vers ce bloc

=== EXEMPLES DE BONNES RÉPONSES ===

Salutation utilisateur (utilise le contenu du bloc welcome mais en mode "text"):
{"type":"text","id_element":null,"id_previous":null,"message":"Bonjour ! Je suis ${botName}, comment puis-je vous aider aujourd'hui ?","preview_url":true}

Demande d'information avec bloc correspondant:
{"type":"redirection","id_element":null,"id_previous":null,"redirection_block":"catalogue-pro-pr1alj"}

Proposition d'options:
{"type":"text_button","id_element":null,"id_previous":null,"message":"Que souhaitez-vous faire ?","buttons":[{"id":"catalogue-pro-pr1alj","title":"Voir catalogue"},{"id":"point-de-coll-ed7kgb","title":"Points collecte"}]}

Affichage d'image produit:
[
  {
    "type": "image",
    "id_element": "img_prod_001",
    "id_previous": null,
    "link": "https://apicommerce.aipeelo.xyz/images/products/product_689220a9ac56ee97afa81097_1755378468106_c92669dbbbaf366d.jpeg"
  },
  {
    "type": "delay",
    "id_element": "delay_001",
    "id_previous": "img_prod_001",
    "timmer": "1.5"
  },
  {
    "type": "text",
    "id_element": "text_001",
    "id_previous": "delay_001",
    "message": "Voici le parfum Manguier Rose à 55 000 XOF",
    "preview_url": true
  }
]

Maintenant, analyse le message de l'utilisateur et réponds avec le JSON approprié.`;
  }

  /**
   * Helper pour formater la liste des blocs en texte
   */
  static formatBlocksToText(blocks) {
      if (!blocks || !Array.isArray(blocks) || blocks.length === 0) return "(Aucun bloc)";
      
      return blocks.map((b, index) => {
         let displayType = b.source;
         if (b.source === 'blocks') displayType = 'standard';
         if (b.source === 'mainblocks' && b.type) displayType = `mainblock_${b.type}`;
         
         let details = `${index + 1}. BLOC "${b.idblock}"\n   Nom: ${b.nom}\n   ${b.commentaire ? `Commentaire: ${b.commentaire}\n   ` : ''}\n   Type: ${displayType}\n   Contenu: ${b.contenu}`;
         if (b.buttons && b.buttons.length > 0) {
             details += `\n   Boutons disponibles: ${b.buttons.map(btn => `"${btn.title}" (ID: ${btn.id})`).join(', ')}`;
         }
         return details;
      }).join('\n\n');
  }
  
  /**
   * Helper pour formater la liste des produits en texte
   */
  static formatProductsToText(products) {
      if (!products || !Array.isArray(products) || products.length === 0) return "(Aucun produit)";
      
      return products.map(p => {
          return `PRODUIT "${p.id}"\n   Titre: ${p.title}\n   Prix: ${p.price} ${p.currency}\n   Stock: ${p.stock}\n   Image: ${p.image}`;
      }).join('\n\n');
  }

  /**
   * Compile le prompt structuré en texte plat pour l'affichage/utilisation
   */
  static compilePrompt(structure, partial = false) {
      if (!structure) return "";
      
      // Si on veut juste les parties dynamiques (pour la vue "Texte" demandée par l'user)
      if (partial) {
          const blocksText = this.formatBlocksToText(structure.blocks);
          return `=== BLOCS DISPONIBLES ===\n\n${blocksText}`;
      }
      
      if (!structure.promptblock) return JSON.stringify(structure, null, 2);
      if (partial) {
          const blocksText = this.formatBlocksToText(structure.blocks);
          // Only show blocks as requested by user in step 820 ("juste faire apparaitre format text de la partie des block")
          // But kept products just in case. User said "partie des block".
          // Let's include products too as "partie des block dans le system prompt" usually implies blocks+products section.
          // But user emphasized BLOCKS. I will include both but structured.
          
          return `=== BLOCS DISPONIBLES ===\n\n${blocksText}`;
      }
      
      let compiled = structure.promptblock;
      
      // Compile Blocks
      compiled = compiled.replace('{{LIST_BLOCKS}}', this.formatBlocksToText(structure.blocks));
      
      // Compile Products
      compiled = compiled.replace('{{LIST_PRODUCTS}}', this.formatProductsToText(structure.products));
      
      return compiled;
  }

  /**
   * Version simplifiée pour compatibilité (ancienne méthode)
   * @param {Object} flowData - Le JSON contenant les données du flow
   * @returns {String} Le système prompt formaté pour Claude
   */
  static generateSystemPrompt(flowData) {
    // Rediriger vers la nouvelle méthode optimisée
    return this.generateOptimizedSystemPrompt(flowData);
  }

  /**
   * Extrait les informations pertinentes de chaque bloc (ancienne méthode pour compatibilité)
   * @param {Array} blocks - Liste des blocs
   * @returns {Array} Informations structurées des blocs
   */
  static extractBlockInformations(blocks) {
    return blocks.map(block => {
      const info = {
        idblock: block.idblock,
        blockname: block.blockname || 'Sans nom',
        type: block.type,
        messages: [],
        buttons: [],
        hasContent: block.flow && block.flow.length > 0
      };

      // Extraire le contenu du flow
      if (block.flow) {
        block.flow.forEach(element => {
          switch (element.type) {
            case 'text':
              if (element.message) {
                info.messages.push(element.message);
              }
              break;
              
            case 'text_button':
              if (element.message) {
                info.messages.push(element.message);
              }
              if (element.buttons) {
                element.buttons.forEach(btn => {
                  info.buttons.push({
                    id: btn.id,
                    title: btn.title
                  });
                });
              }
              break;
              
            case 'list':
              if (element.interactive?.header?.text) {
                info.messages.push(element.interactive.header.text);
              }
              if (element.interactive?.body?.text) {
                info.messages.push(element.interactive.body.text);
              }
              break;
          }
        });
      }
      
      return info;
    });
  }

  /**
   * Construit le prompt système final (ancienne méthode pour compatibilité)
   * @param {Array} blockInfos - Informations des blocs
   * @param {Object} mainBlocks - Blocs principaux
   * @returns {String} Prompt système formaté
   */
  static buildSystemPrompt(blockInfos, mainBlocks) {
    const activeBlocks = blockInfos.filter(block => block.hasContent);
    
    let prompt = `Tu es un assistant IA utilisé dans une plateforme de création de chatbot pour réorienter et répondre à des questions lorsque l'utilisateur sort du scénario prévu

`;

    // Ajouter les informations de chaque bloc
    activeBlocks.forEach(block => {
      prompt += `BLOC: ${block.blockname} (ID: ${block.idblock})
- Type: ${block.type}`;

      if (block.messages.length > 0) {
        prompt += `
- Contenu: ${block.messages.join('\n\n')}`;
      }

      if (block.buttons.length > 0) {
        prompt += `
- Boutons: ${block.buttons.map(btn => btn.title).join(', ')}`;
      }
      prompt += '\n\n';
    });

    prompt += `INSTRUCTIONS:
1. Analyse le message de l'utilisateur
2. Identifie le bloc le plus pertinent basé sur le contenu
3. Retourne UNIQUEMENT un JSON valide au format suivant:

{
  "type": "redirection",
  "id_element": null,
  "id_previous": null,
  "redirection_block": "idblock_correspondant"
}

RÈGLES:
- Si aucun bloc ne correspond clairement, utilise le bloc par défaut
- Sois précis dans la correspondance du contenu
- Priorise les correspondances exactes sur le contenu
- Ne retourne que le JSON, aucun autre texte

BLOCS SPÉCIAUX:
- Bloc d'accueil: ${mainBlocks.welcome?.[0]?.idblock || 'Non défini'}
- Bloc par défaut: ${mainBlocks.default?.[0]?.idblock || 'Non défini'}
- Bloc commerce: ${mainBlocks.commerce?.[0]?.idblock || 'Non défini'}`;

    return prompt;
  }

  /**
   * Génère une fonction de test pour valider le prompt
   * @param {Object} flowData - Données du flow
   * @returns {Function} Fonction de test
   */
  static createTestFunction(flowData) {
    const systemPrompt = this.generateOptimizedSystemPrompt(flowData);
    
    return async function testClaudeRedirection(userMessage, claudeApiKey) {
      if (!claudeApiKey) {
        throw new Error('Clé API Claude requise pour les tests');
      }

      try {
        const response = await fetch('https://api.anthropic.com/v1/messages', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': claudeApiKey,
            'anthropic-version': '2023-06-01'
          },
          body: JSON.stringify({
            model: 'claude-3-sonnet-20240229',
            max_tokens: 1000,
            system: systemPrompt,
            messages: [
              {
                role: 'user',
                content: userMessage
              }
            ]
          })
        });

        if (!response.ok) {
          throw new Error(`Erreur API Claude: ${response.status}`);
        }

        const data = await response.json();
        const result = data.content[0].text;
        
        try {
          return JSON.parse(result);
        } catch (e) {
          console.error('Réponse Claude non-JSON:', result);
          throw new Error('Réponse Claude invalide');
        }
      } catch (error) {
        console.error('Erreur lors du test Claude:', error);
        throw error;
      }
    };
  }

  /**
   * Génère des exemples de test basés sur le contenu des blocs
   * @param {Object} flowData - Données du flow
   * @returns {Array} Exemples de test
   */
  static generateTestExamples(flowData) {
    const blockInfos = this.extractDetailedBlockInformations(
      flowData.flow.blocks || [],
      flowData.flow.mainblocks || {}
    );
    const examples = [];

    // Exemples de salutations
    examples.push(
      { userMessage: "Bonjour", expectedType: "text", description: "Test salutation" },
      { userMessage: "Salut", expectedType: "text", description: "Test salutation informelle" },
      { userMessage: "Bonsoir", expectedType: "text", description: "Test salutation soir" }
    );

    // Exemples basés sur les blocs
    blockInfos.forEach(block => {
      if (block.textMessages.length > 0) {
        // Créer un exemple basé sur les premiers mots du bloc
        const firstWords = block.textMessages[0]
          .replace(/\{\{[^}]+\}\}/g, '')
          .replace(/\*/g, '')
          .split(' ')
          .slice(0, 3)
          .join(' ')
          .trim();
          
        if (firstWords.length > 2) {
          examples.push({
            userMessage: firstWords,
            expectedBlock: block.idblock,
            expectedType: "redirection",
            description: `Test pour le bloc ${block.blockname}`
          });
        }
      }

      // Exemples basés sur les boutons
      block.buttons.forEach(button => {
        examples.push({
          userMessage: button.title,
          expectedBlock: button.id,
          expectedType: "redirection",
          description: `Test pour le bouton ${button.title}`
        });
      });
    });

    // Exemples de demandes spéciales
    examples.push(
      { userMessage: "Je veux parler à un agent", expectedType: "text_button", description: "Test demande agent" },
      { userMessage: "Aidez-moi", expectedType: "text", description: "Test demande d'aide générale" },
      { userMessage: "Connard", expectedType: "text", description: "Test insulte" }
    );

    return examples;
  }

  /**
   * Valide la structure d'un bloc pour s'assurer qu'il peut être utilisé
   * @param {Object} block - Le bloc à valider
   * @returns {Object} Résultat de validation
   */
  static validateBlock(block) {
    const validation = {
      isValid: true,
      errors: [],
      warnings: []
    };

    if (!block.idblock) {
      validation.isValid = false;
      validation.errors.push('idblock manquant');
    }

    if (!block.flow || !Array.isArray(block.flow)) {
      validation.warnings.push('Aucun flow défini');
    }

    if (block.flow && block.flow.length === 0) {
      validation.warnings.push('Flow vide');
    }

    // Vérifier les éléments du flow
    if (block.flow) {
      block.flow.forEach((element, index) => {
        if (!element.type) {
          validation.errors.push(`Élément ${index}: type manquant`);
          validation.isValid = false;
        }

        if (element.type === 'text_button' && element.buttons) {
          element.buttons.forEach((btn, btnIndex) => {
            if (!btn.id) {
              validation.errors.push(`Bouton ${btnIndex}: ID manquant`);
              validation.isValid = false;
            }
            if (!btn.title) {
              validation.warnings.push(`Bouton ${btnIndex}: titre manquant`);
            }
            if (btn.title && btn.title.length > 20) {
              validation.warnings.push(`Bouton ${btnIndex}: titre trop long (${btn.title.length} > 20)`);
            }
          });
        }
      });
    }

    return validation;
  }
}

// Fonction utilitaire pour usage direct
export function generateClaudePrompt(flowData) {
  return ClaudePromptGenerator.generateOptimizedSystemPrompt(flowData);
}

// Exemple d'utilisation
export function createClaudeIntegration(flowData, apiKey) {
  const testFunction = ClaudePromptGenerator.createTestFunction(flowData);
  const examples = ClaudePromptGenerator.generateTestExamples(flowData);
  
  return {
    systemPrompt: ClaudePromptGenerator.generateOptimizedSystemPrompt(flowData),
    testFunction,
    examples,
    
    // Fonction pour tester rapidement
    async quickTest(userMessage) {
      return await testFunction(userMessage, apiKey);
    }
  };
}
